import React from 'react';
import { Box, Typography, LinearProgress, Tooltip, IconButton, useTheme } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { StageDetails } from '../../types/processFlowTypes';

interface ProcessHeaderProps {
  stageDetails: Record<string, StageDetails>;
  currentStage: string;
  title?: string;
  description?: string;
}

const ProcessHeader: React.FC<ProcessHeaderProps> = ({
  stageDetails,
  currentStage,
  title = "Keyword Analysis Process",
  description = "Follow the steps to classify and process your keywords"
}) => {
  const theme = useTheme();
  const stageIds = Object.keys(stageDetails);
  const currentIndex = stageIds.findIndex(id => id === currentStage);
  const progress = ((currentIndex + 1) / stageIds.length) * 100;

  return (
    <Box sx={{ 
      mb: 3, 
      px: 4, 
      py: 2,
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center' 
    }}>
      <Box sx={{ 
        display: 'flex', 
        width: '100%', 
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1
      }}>
        <Typography 
          variant="h5" 
          component="h1" 
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundSize: '100%',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            MozBackgroundClip: 'text',
            MozTextFillColor: 'transparent',
            display: 'inline-block',
            fontWeight: 700,
            letterSpacing: '0.02em'
          }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Stage {currentIndex + 1} of {stageIds.length}
          </Typography>
          <Tooltip title="View process help">
            <IconButton size="small" color="primary">
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
        {description}
      </Typography>
      
      <LinearProgress 
        variant="determinate" 
        value={progress} 
        sx={{ 
          width: '100%', 
          height: 8, 
          borderRadius: 4,
          backgroundColor: theme.palette.grey[200],
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
          }
        }} 
      />
    </Box>
  );
};

export default ProcessHeader;
